import { useMemo, useState } from 'react';

import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  Link,
  TextField,
  Typography,
} from '@mui/material';
import { useFormik } from 'formik';
import { useMutation } from 'react-query';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import Test from 'react-test-attributes';
import { setBannerMessage } from 'src/store/display';
import { PlanPrice } from 'src/types/plans';
import {
  getInviteState,
  getInviteToken,
} from 'src/utils/hooks/walletInvitation';
import * as Yup from 'yup';

import EmailDialog from './EmailDialog';
import {
  createUserEmail,
  refreshConfimationEmail,
} from '../../../services/auth';
import * as S from '../styles';

const errorTypesToShowMessage = ['ValidationError', 'ForbiddenError'] as const;

type EmailState = {
  id: number;
  email: string;
};

type WalletInvitationData = {
  email?: string;
  [key: string]: any;
};

interface EmailConfirmationFormProps {
  email: string;
  userType: string;
}

function EmailConfirmationForm({
  email,
  userType,
}: EmailConfirmationFormProps) {
  const dispatch = useDispatch();
  const history = useHistory();
  const state = history.location?.state as {
    planPrice?: PlanPrice;
    planCount: number;
  };
  const planPriceId = state?.planPrice?.priceId;
  const planCount = state?.planCount;
  const [openDialog, setOpenDialog] = useState(false);
  const [emailData, setEmailData] = useState<EmailState>();
  const invitationState = getInviteState() as WalletInvitationData | null;
  const invitationToken = getInviteToken();
  const invitationEmail = invitationState?.email;
  const isFromWalletInvitation = useMemo(
    () =>
      Boolean(invitationToken && invitationEmail && invitationEmail !== email),
    [email, invitationEmail, invitationToken],
  );

  const ValidateForm = Yup.object().shape({
    email: Yup.string()
      .required('Campo obrigatório')
      .email('Digite um e-mail válido'),
  });

  const handleClose = () => {
    history.push('/plataforma');
    setOpenDialog(false);
  };

  const formik = useFormik({
    initialValues: { email: invitationEmail || email },
    validationSchema: ValidateForm,
    enableReinitialize: true,
    onSubmit: (values) => {
      emailConfirmation.mutateAsync({
        email: values.email,
        userType,
        planPriceId,
        planCount,
      });
    },
  });

  const refreshEmail = useMutation(refreshConfimationEmail, {
    onError: (error: any) => {
      if (
        error &&
        errorTypesToShowMessage.includes(error?.response?.data?.error?.name)
      ) {
        formik.setErrors({ email: error?.response?.data?.error?.message });
      } else {
        dispatch(
          setBannerMessage({
            message: 'Erro ao validar cadastro do e-mail, tente novamente.',
            type: 'error',
            autoclose: true,
          }),
        );
      }
    },
    onSuccess: async () => {
      setOpenDialog(true);
    },
  });

  const emailConfirmation = useMutation(createUserEmail, {
    onError: (error: any) => {
      if (error) {
        if (
          errorTypesToShowMessage.includes(error?.response?.data?.error?.name)
        ) {
          formik.setErrors({ email: error?.response?.data?.error?.message });
        } else {
          dispatch(
            setBannerMessage({
              message: 'Erro ao validar cadastro do e-mail, tente novamente.',
              type: 'error',
              autoclose: true,
            }),
          );
        }
      }
    },
    onSuccess: async ({ data }) => {
      setEmailData(data);
      setOpenDialog(true);
    },
  });

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      sx={{ padding: '130px 0', maxWidth: 400, margin: '0 auto' }}>
      <Typography textAlign="center" component="h1" variant="h3">
        Crie sua conta
      </Typography>

      <Typography textAlign="center" sx={{ marginTop: 7 }}>
        Não se preocupe, para aproveitar o período de teste não é necessário
        cadastrar cartão de crédito.
      </Typography>

      {isFromWalletInvitation && (
        <Typography
          textAlign="center"
          sx={{ marginTop: 3, color: '#1976d2', fontWeight: 500 }}>
          Este cadastro está vinculado ao convite de ambiente recebido.
        </Typography>
      )}

      <Box pt={4} pb={7}>
        <form onSubmit={formik.handleSubmit}>
          <Grid container alignItems="center" gap={8}>
            <Grid item xs={12}>
              <S.FieldTitle>E-mail*</S.FieldTitle>
              <FormControl fullWidth variant="outlined">
                {/* @ts-ignore */}
                <Test id="register-form-email">
                  <TextField
                    name="email"
                    placeholder="Insira seu e-mail corporativo aqui"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    error={formik.touched.email && Boolean(formik.errors.email)}
                    helperText={
                      formik.touched.email && formik.errors.email
                        ? formik.errors.email
                        : isFromWalletInvitation
                          ? 'E-mail do convite de ambiente'
                          : ''
                    }
                    disabled={isFromWalletInvitation}
                  />
                </Test>
              </FormControl>
            </Grid>
            <Grid item xs={12} container justifyContent="center">
              {/* @ts-ignore */}
              <Test id="register-button-submitemail">
                <Button
                  color="primary"
                  variant="contained"
                  size="large"
                  type="submit">
                  {emailConfirmation.isLoading ? (
                    <CircularProgress sx={{ color: '#FFFFFF' }} />
                  ) : (
                    'Cadastrar e-mail'
                  )}
                </Button>
              </Test>
            </Grid>
          </Grid>
        </form>
      </Box>
      <Typography>
        Já tem uma conta? <Link href="/login">Faça o login aqui</Link>
      </Typography>
      <EmailDialog
        open={openDialog}
        handleAction={() => refreshEmail.mutateAsync({ id: emailData?.id })}
        onClose={handleClose}
      />
    </Box>
  );
}

export { EmailConfirmationForm };
